import { AlertSelectedContextCard, isContextPending } from './ContextCards';
import { findInvalidEmails, parseReceiverEmails } from './receiverEmails';
import {
    ArrowLeft,
    CirclePlus,
    Copy,
    MoreVertical,
    Trash2,
} from 'lucide-react';
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Input,
    Label,
    RadioGroup,
    RadioGroupItem,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    Text,
} from '@/ui/design-system/components';
import {
    COUNTRIES_ORDER,
    DATA_SOURCES,
    TIKTOK_COUNTRIES,
} from '@/config/constants';
import { Link, useHistory } from 'react-router-dom';
import {
    useAlertCategoryCoverage,
    useAlertContexts,
    useUpdateAlertContext,
} from '@/utils/hooks/alerts';
import { useEffect, useMemo, useRef, useState } from 'react';
import CategoryMultiSelect from '@/ui/components/CategoryMultiSelect';
import ContextEmptyStateModal from './ContextEmptyStateModal';
import ContextModal from './ContextModal';
import RecipientEmailsInput from './RecipientEmailsInput';
import DeleteAlertConfirmModal from '@/ui/components/modals/alerts/DeleteAlertConfirmModal';
import NiceModal from '@ebay/nice-modal-react';
import { SidebarTrigger } from '@/ui/design-system/components/ui/sidebar';
import { useAuth0 } from '@auth0/auth0-react';
import { useFetchTikTokCountries } from 'utils/hooks';

const ALERTS_COUNTRY_ORDER = COUNTRIES_ORDER.filter((code) =>
    TIKTOK_COUNTRIES.includes(code),
);
const MAX_CONTEXTS_PER_ALERT = 3;
const ALERT_DATA_SOURCE_OPTIONS = [
    { value: 'tiktok', label: 'TikTok', coverageSource: DATA_SOURCES.TIKTOK },
    {
        value: 'popularity',
        label: 'Pop Index',
        coverageSource: DATA_SOURCES.POPULARITY,
    },
];

const TREND_TYPE_LABELS = {
    tiktok: [
        {
            value: 'nano',
            label: 'Trending This Week',
            description: 'Micro-viral sparks, early signals.',
        },
        {
            value: 'micro',
            label: 'Trending This Quarter',
            description: 'Sustained cultural movements gaining traction.',
        },
        {
            value: 'macro',
            label: 'Trending This Year',
            description: 'Established, long-term trends.',
        },
    ],
    popularity: [
        {
            value: 'nano',
            label: 'Early',
            description:
                'Speculative, high-reward trends before the mass market catches on.',
        },
        {
            value: 'micro',
            label: 'Growth',
            description:
                'Trends to watch. On the rise and headed for mainstream.',
        },
        {
            value: 'macro',
            label: 'Mainstream',
            description:
                'Category drivers with massive awareness and no sign of slowing.',
        },
    ],
};

const normalizeCadenceForDataSource = (cadence, dataSource) => {
    if (dataSource === 'popularity') return 'monthly';
    return cadence || 'weekly';
};

function AlertConfigForm({
    config,
    onSubmit,
    onDelete,
    isSubmitting,
    isDeleting,
    onConfigChange,
}) {
    const { user } = useAuth0();
    const history = useHistory();
    const categoriesCountRef = useRef();
    const contextsCountRef = useRef();
    const emailsCountRef = useRef();
    const autoRegeneratedContextIdsRef = useRef(new Set());
    const { data: countriesData } = useFetchTikTokCountries();
    const configCountry = config?.country || null;
    const marketOptions = useMemo(() => {
        if (!countriesData) return [];
        const options = ALERTS_COUNTRY_ORDER.map((code) => {
            const country = countriesData.find((item) => item.text_id === code);
            if (!country) return null;
            return {
                value: country.text_id,
                label: country.name,
            };
        }).filter(Boolean);

        if (
            configCountry &&
            !options.some((option) => option.value === configCountry)
        ) {
            options.unshift({
                value: configCountry,
                label: configCountry,
            });
        }

        return options;
    }, [countriesData, configCountry]);
    const initialDataSource = config?.data_source || 'tiktok';
    const initialCadence = normalizeCadenceForDataSource(
        config ? config.cadence : 'weekly',
        initialDataSource,
    );
    const [formData, setFormData] = useState({
        categories: config?.categories || [],
        trend_type: config?.trend_type || 'micro',
        cadence: initialCadence,
        country:
            configCountry || ALERTS_COUNTRY_ORDER[0] || TIKTOK_COUNTRIES[0],
        receiver_emails: parseReceiverEmails(config?.receiver_emails).length
            ? parseReceiverEmails(config?.receiver_emails)
            : parseReceiverEmails(user?.email),
        name: config?.name || 'Trend update',
        data_source: config?.data_source || 'tiktok',
        paused: config?.paused ?? false,
    });
    const [selectedContextIds, setSelectedContextIds] = useState(
        config?.context_ids || [],
    );
    const [contextModalOpen, setContextModalOpen] = useState(false);
    const [emptyContextModalOpen, setEmptyContextModalOpen] = useState(false);
    const { data: contextsData, isLoading: contextsLoading } =
        useAlertContexts();
    const regenerateContext = useUpdateAlertContext();
    const contexts = contextsData?.contexts || [];
    const selectedContexts = useMemo(
        () =>
            contexts.filter((ctx) =>
                selectedContextIds.includes(ctx.alert_context_id),
            ),
        [contexts, selectedContextIds],
    );
    const hasAvailableContexts =
        contexts.length > 0 || selectedContextIds.length > 0;
    const hasTooManyContexts =
        selectedContextIds.length > MAX_CONTEXTS_PER_ALERT;
    const invalidRecipients = findInvalidEmails(formData.receiver_emails);

    useEffect(() => {
        setSelectedContextIds(config?.context_ids || []);
    }, [config?.alert_config_id]);

    useEffect(() => {
        if (contextsLoading) return;

        const pendingSelectedContexts = selectedContexts.filter(
            (ctx) =>
                isContextPending(ctx) &&
                !autoRegeneratedContextIdsRef.current.has(ctx.alert_context_id),
        );

        if (!pendingSelectedContexts.length) return;

        pendingSelectedContexts.forEach((ctx) => {
            autoRegeneratedContextIdsRef.current.add(ctx.alert_context_id);
            regenerateContext
                .mutateAsync({
                    alertContextId: ctx.alert_context_id,
                    data: { regenerate: true },
                })
                .catch((error) => {
                    console.error('Failed to auto-regenerate context:', error);
                    autoRegeneratedContextIdsRef.current.delete(
                        ctx.alert_context_id,
                    );
                });
        });
    }, [contextsLoading, regenerateContext, selectedContexts]);

    const previewConfig = useMemo(() => {
        const primaryContext = selectedContexts[0];
        return {
            ...config,
            name: formData.name,
            brand_name:
                primaryContext?.name || config?.brand_name || formData.name,
            website_url: primaryContext?.website_url || '',
            prompt: primaryContext?.instructions || '',
            categories: formData.categories,
            trend_type: formData.trend_type,
            cadence: formData.cadence,
            country: formData.country,
            data_source: formData.data_source,
            context_ids: selectedContextIds,
        };
    }, [config, formData, selectedContextIds, selectedContexts]);

    useEffect(() => {
        onConfigChange?.(previewConfig);
    }, [onConfigChange, previewConfig]);
    const {
        categories: coveredCategories,
        isLoading: categoriesLoading,
        error: categoriesError,
        unsupportedSelections,
        hasUnsupportedSelections,
    } = useAlertCategoryCoverage({
        country: formData.country,
        selectedCategories: formData.categories,
        dataSource:
            ALERT_DATA_SOURCE_OPTIONS.find(
                (option) => option.value === formData.data_source,
            )?.coverageSource || DATA_SOURCES.TIKTOK,
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (hasUnsupportedSelections) {
            return;
        }
        if (!selectedContextIds.length) {
            contextsCountRef.current?.setCustomValidity?.(
                'Please select at least one context.',
            );
            return;
        }
        if (hasTooManyContexts) {
            contextsCountRef.current?.setCustomValidity?.(
                `You can select up to ${MAX_CONTEXTS_PER_ALERT} contexts.`,
            );
            return;
        }
        const primaryContext = selectedContexts[0];
        const brandName =
            primaryContext?.name || config?.brand_name || formData.name;
        const websiteUrl = primaryContext?.website_url || '';
        const prompt = primaryContext?.instructions || '';
        onSubmit({
            brand_name: brandName,
            website_url: websiteUrl,
            categories: formData.categories,
            trend_type: formData.trend_type,
            cadence: formData.cadence,
            country: formData.country,
            name: formData.name,
            prompt,
            data_source: formData.data_source,
            paused: formData.paused,
            context_ids: selectedContextIds,
            receiver_emails: formData.receiver_emails,
        });
    };

    const handleDeleteClick = async () => {
        const confirmed = await NiceModal.show(DeleteAlertConfirmModal, {
            alertName: config?.name || 'this alert',
        });

        if (confirmed && onDelete) {
            onDelete();
        }
    };

    const handleDuplicateClick = () => {
        // Navigate to create page with saved config (not unsaved formData)
        history.push('/alerts/new', {
            duplicateFrom: {
                brand_name: config.brand_name,
                website_url: config.website_url,
                categories: config.categories,
                trend_type: config.trend_type,
                country: config.country,
                receiver_emails: parseReceiverEmails(config.receiver_emails),
                name: `${config.name} (Copy)`,
                prompt: config.prompt,
                cadence: normalizeCadenceForDataSource(
                    config.cadence,
                    config.data_source,
                ),
                data_source: config.data_source,
                context_ids: config.context_ids || [],
            },
        });
    };

    // Check if form has changes (for edit mode)
    const hasChanges = useMemo(() => {
        if (!config) return true; // Always allow submit for new alerts

        // Compare emails
        const currentEmails = parseReceiverEmails(formData.receiver_emails)
            .sort()
            .join(',');
        const originalEmails = parseReceiverEmails(config.receiver_emails)
            .sort()
            .join(',');

        // Compare categories (sort by id since order doesn't matter)
        const currentCategories = [...formData.categories].sort(
            (a, b) => a.id - b.id,
        );
        const originalCategories = [...(config.categories || [])].sort(
            (a, b) => a.id - b.id,
        );
        const currentCountry = formData.country || '';
        const originalCountry = config.country || '';

        const currentContextIds = [...(selectedContextIds || [])]
            .sort()
            .join(',');
        const originalContextIds = [...(config.context_ids || [])]
            .sort()
            .join(',');

        return (
            formData.name !== (config.name || '') ||
            formData.data_source !== (config.data_source || 'tiktok') ||
            formData.trend_type !== (config.trend_type || 'micro') ||
            formData.cadence !== config.cadence ||
            currentCountry !== originalCountry ||
            formData.paused !== (config.paused ?? false) ||
            currentEmails !== originalEmails ||
            JSON.stringify(currentCategories) !==
                JSON.stringify(originalCategories) ||
            currentContextIds !== originalContextIds
        );
    }, [config, formData, selectedContextIds]);

    const handleContextCreated = (createdContext) => {
        const createdContextId = createdContext?.alert_context_id;
        if (!createdContextId) {
            return;
        }
        contextsCountRef.current?.setCustomValidity?.('');
        setSelectedContextIds((previous) => {
            if (previous.includes(createdContextId)) {
                return previous;
            }
            if (previous.length >= MAX_CONTEXTS_PER_ALERT) {
                return previous;
            }
            return [...previous, createdContextId];
        });
    };

    const handleContextRemove = (contextId) => {
        contextsCountRef.current?.setCustomValidity?.('');
        setSelectedContextIds((previous) =>
            previous.filter((id) => id !== contextId),
        );
    };

    return (
        <div className="md:relative md:border-r border-base-card">
            <form
                onSubmit={handleSubmit}
                className="grid grid-rows-[auto_1fr_auto] md:absolute md:inset-0"
            >
                <header className="flex items-center justify-between px-5 py-4 border-b border-base-card">
                    <div className="flex items-center gap-5">
                        <SidebarTrigger className="md:hidden" />
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9"
                            asChild
                        >
                            <Link to="/alerts">
                                <ArrowLeft
                                    className="h-4 w-4"
                                    aria-label="Back to alerts"
                                />
                            </Link>
                        </Button>
                        <Text as="span" className="font-semibold">
                            {config ? config.name : 'New alert'}
                        </Text>
                    </div>
                    <div className="flex items-center gap-3">
                        {config ? (
                            <>
                                <Switch
                                    checked={!formData.paused}
                                    onCheckedChange={(checked) =>
                                        setFormData({
                                            ...formData,
                                            paused: !checked,
                                        })
                                    }
                                />
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9"
                                        >
                                            <MoreVertical className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                handleDuplicateClick();
                                            }}
                                        >
                                            <Copy className="h-4 w-4" />
                                            Duplicate
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            disabled={isDeleting}
                                            onSelect={(e) => {
                                                e.preventDefault();
                                                handleDeleteClick();
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            Delete alert
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : null}
                    </div>
                </header>
                <main className="md:overflow-y-auto px-5 py-4">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="name">Alert Name</Label>
                            <Input
                                id="name"
                                type="text"
                                value={formData.name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                                required
                                placeholder="e.g., Skincare Trends Alert"
                            />
                        </div>
                        <div className="flex flex-col gap-2">
                            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                            <Label className="text-sm font-medium text-foreground">
                                Context
                            </Label>
                            <Text as="span" size="sm" color="muted">
                                Guides how trends are selected and analyzed for
                                this alert. Choose a brand, topic, or both.
                            </Text>
                            {selectedContexts.length ? (
                                <div className="flex flex-col gap-2">
                                    {selectedContexts.map((context) => (
                                        <AlertSelectedContextCard
                                            key={context.alert_context_id}
                                            context={context}
                                            onRemove={handleContextRemove}
                                        />
                                    ))}
                                </div>
                            ) : null}
                            <Button
                                type="button"
                                variant="outline"
                                className="h-9 w-fit rounded-md px-4 font-medium"
                                onClick={() => {
                                    if (!hasAvailableContexts) {
                                        setEmptyContextModalOpen(true);
                                        return;
                                    }
                                    setContextModalOpen(true);
                                }}
                                disabled={contextsLoading}
                            >
                                <CirclePlus className="h-4 w-4 mr-2" />
                                {hasAvailableContexts
                                    ? 'Select context'
                                    : 'Create new context'}
                            </Button>
                            <div className="relative w-full h-0 overflow-hidden flex justify-center">
                                <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    required
                                    value={selectedContextIds.length}
                                    ref={contextsCountRef}
                                    onInvalid={() => {
                                        contextsCountRef.current.setCustomValidity(
                                            'Please select at least one context.',
                                        );
                                    }}
                                    css={`
                                        position: absolute;
                                        width: 1px;
                                        height: 1px;
                                        margin: -1px;
                                        padding: 0;
                                        overflow: hidden;
                                        clip: rect(0, 0, 0, 0);
                                        clip-path: inset(50%);
                                        border: 0;
                                        white-space: nowrap;
                                    `}
                                    tabIndex={-1}
                                />
                            </div>
                            {hasTooManyContexts ? (
                                <Text
                                    as="span"
                                    size="sm"
                                    className="text-destructive"
                                >
                                    You can select up to{' '}
                                    {MAX_CONTEXTS_PER_ALERT} contexts per alert.
                                </Text>
                            ) : null}
                            <div className="mt-2 border-t border-dashed border-[#D9D9D9]" />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="country">Market</Label>
                            <Select
                                value={formData.country}
                                onValueChange={(country) =>
                                    setFormData({ ...formData, country })
                                }
                            >
                                <SelectTrigger id="country">
                                    <SelectValue placeholder="Select a market..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {marketOptions.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-2">
                            <CategoryMultiSelect
                                label="Categories"
                                value={formData.categories}
                                onChange={(categories) => {
                                    categoriesCountRef.current.setCustomValidity(
                                        '',
                                    );
                                    setFormData({ ...formData, categories });
                                }}
                                placeholder="Select categories..."
                                categories={coveredCategories}
                                isLoading={categoriesLoading}
                                error={categoriesError}
                            />
                            {hasUnsupportedSelections ? (
                                <Text
                                    as="span"
                                    size="sm"
                                    className="text-destructive"
                                >
                                    Some selected categories aren’t available in{' '}
                                    {formData.country}. Remove:{' '}
                                    {unsupportedSelections.join(', ')}
                                </Text>
                            ) : null}
                            <div className="relative w-full h-0 overflow-hidden flex justify-center">
                                <input
                                    type="number"
                                    min={1}
                                    step={1}
                                    required
                                    value={formData.categories.length}
                                    ref={categoriesCountRef}
                                    onInvalid={() => {
                                        categoriesCountRef.current.setCustomValidity(
                                            'Please select at least one category.',
                                        );
                                    }}
                                    css={`
                                        position: absolute;
                                        width: 1px;
                                        height: 1px;
                                        margin: -1px;
                                        padding: 0;
                                        overflow: hidden;
                                        clip: rect(0, 0, 0, 0);
                                        clip-path: inset(50%);
                                        border: 0;
                                        white-space: nowrap;
                                    `}
                                    tabIndex={-1}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                            <Label>Data source</Label>
                            <Select
                                value={formData.data_source}
                                onValueChange={(data_source) => {
                                    setFormData({
                                        ...formData,
                                        data_source,
                                        cadence: normalizeCadenceForDataSource(
                                            formData.cadence,
                                            data_source,
                                        ),
                                    });
                                }}
                            >
                                <SelectTrigger id="data_source">
                                    <SelectValue placeholder="Select a data source..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {ALERT_DATA_SOURCE_OPTIONS.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-3">
                            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
                            <Label>
                                {formData.data_source === 'popularity'
                                    ? 'Life cycle'
                                    : 'Trend type'}
                            </Label>
                            <RadioGroup
                                value={formData.trend_type}
                                onValueChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        trend_type: value,
                                    })
                                }
                            >
                                {(
                                    TREND_TYPE_LABELS[formData.data_source] ||
                                    TREND_TYPE_LABELS.tiktok
                                ).map((option) => (
                                    <RadioGroupItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        <div className="flex flex-col gap-1.5 pt-px">
                                            <Text
                                                as="span"
                                                className="font-medium leading-none"
                                            >
                                                {option.label}
                                            </Text>
                                            <Text
                                                as="span"
                                                size="sm"
                                                color="muted"
                                                className="leading-5"
                                            >
                                                {option.description}
                                            </Text>
                                        </div>
                                    </RadioGroupItem>
                                ))}
                            </RadioGroup>
                        </div>
                        <input
                            type="hidden"
                            name="data_source"
                            value={formData.data_source}
                        />
                        {/* Receiver Emails */}
                        <div className="flex flex-col gap-2">
                            <RecipientEmailsInput
                                value={formData.receiver_emails}
                                onChange={(emails) => {
                                    emailsCountRef.current?.setCustomValidity?.(
                                        '',
                                    );
                                    setFormData({
                                        ...formData,
                                        receiver_emails: emails,
                                    });
                                }}
                            />
                            {/* Hidden mirror input so the form enforces at least
                                one recipient via native validation, matching the
                                contexts count field above. */}
                            <input
                                type="number"
                                min={1}
                                step={1}
                                required
                                value={formData.receiver_emails.length}
                                ref={emailsCountRef}
                                onInvalid={() =>
                                    emailsCountRef.current.setCustomValidity(
                                        'Please add at least one recipient.',
                                    )
                                }
                                onChange={() => {}}
                                className="sr-only"
                                tabIndex={-1}
                            />
                            {invalidRecipients.length > 0 ? (
                                <Text
                                    as="span"
                                    size="sm"
                                    className="text-destructive"
                                >
                                    Please remove invalid{' '}
                                    {invalidRecipients.length > 1
                                        ? 'recipients'
                                        : 'recipient'}
                                    : {invalidRecipients.join(', ')}
                                </Text>
                            ) : null}
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label htmlFor="cadence">Frequency</Label>
                            <Select
                                value={formData.cadence}
                                disabled={formData.data_source === 'popularity'}
                                onValueChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        cadence: value,
                                    })
                                }
                            >
                                <SelectTrigger id="cadence">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {formData.data_source !== 'popularity' ? (
                                        <SelectItem value="weekly">
                                            Weekly
                                        </SelectItem>
                                    ) : null}
                                    <SelectItem value="monthly">
                                        Monthly
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </main>
                <footer className="flex items-center justify-between px-5 py-5 border-t border-base-card">
                    <Button type="button" variant="outline" asChild>
                        <Link to="/alerts">Cancel</Link>
                    </Button>
                    <Button
                        type="submit"
                        disabled={
                            isSubmitting ||
                            !hasChanges ||
                            hasUnsupportedSelections ||
                            !selectedContextIds.length ||
                            hasTooManyContexts ||
                            invalidRecipients.length > 0 ||
                            contextsLoading
                        }
                    >
                        {isSubmitting
                            ? 'Saving...'
                            : config
                              ? 'Save changes'
                              : 'Create Alert'}
                    </Button>
                </footer>
            </form>
            <ContextModal
                open={contextModalOpen}
                contexts={contexts}
                selectedContextIds={selectedContextIds}
                maxSelected={MAX_CONTEXTS_PER_ALERT}
                onApplySelection={(next) => {
                    contextsCountRef.current?.setCustomValidity?.('');
                    setSelectedContextIds(next);
                }}
                onOpenChange={(isOpen) => {
                    setContextModalOpen(isOpen);
                }}
            />
            <ContextEmptyStateModal
                open={emptyContextModalOpen}
                onCreated={handleContextCreated}
                onOpenChange={setEmptyContextModalOpen}
            />
        </div>
    );
}

export default AlertConfigForm;
